import { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Upload, Image as ImageIcon, Loader2, Check, X, AlertTriangle, Bug } from "lucide-react";
import { isHeicImage, normalizeImageFile } from "@/lib/heic";
import { downscaleDataUrl, downscaleFile } from "@/lib/imageDownscale";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { track } from "@/lib/posthog";
import { useDebugMode } from "@/hooks/useDebugMode";
import { useDebugPanel } from "@/hooks/useDebugPanel";

interface PhotoUploadProps {
  onUpload: (file: File, preview: string) => void;
}

type IssueCode =
  | "not_a_room"
  | "too_dark"
  | "too_blurry"
  | "too_close_up"
  | "heavy_clutter";

const ISSUE_COPY: Record<IssueCode, { title: string; tip: string }> = {
  not_a_room: {
    title: "This doesn't look like a photo of a room.",
    tip: "Upload a wide shot of the actual space — not a floor plan, product photo or screenshot.",
  },
  too_dark: {
    title: "The room looks quite dark.",
    tip: "Turn on the lights or shoot during the day so we can see the walls and floor.",
  },
  too_blurry: {
    title: "The photo is a little blurry.",
    tip: "Hold your phone steady, tap to focus, and take it again.",
  },
  too_close_up: {
    title: "We can only see part of the room.",
    tip: "Try standing in the doorway so we can see more of the room.",
  },
  heavy_clutter: {
    title: "Clutter is hiding most of the floor and walls.",
    tip: "Move a few things out of the way so the shape of the room is visible.",
  },
};

type PendingPhoto = { file: File; dataUrl: string; issues: IssueCode[] };

type CheckDebug = {
  usable: boolean;
  isRoom?: boolean;
  imageType?: string | null;
  checked?: boolean;
  issues: IssueCode[];
  debug?: unknown;
  client_ms?: number;
};

const PhotoUpload = ({ onUpload }: PhotoUploadProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isConverting, setIsConverting] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [pending, setPending] = useState<PendingPhoto | null>(null);
  const hasDebugRole = useDebugMode();
  const debugPanelOpen = useDebugPanel();
  // Only surface diagnostics when the sidebar debug toggle is actually on.
  const debugMode = hasDebugRole && debugPanelOpen;
  const [checkDebug, setCheckDebug] = useState<CheckDebug | null>(null);
  const [heldPhoto, setHeldPhoto] = useState<{ file: File; dataUrl: string } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Tracks whether the user began an upload attempt without completing it,
  // so we can fire `image_upload_abandoned` on unmount / error.
  const uploadStartedRef = useRef(false);

  useEffect(() => {
    return () => {
      if (uploadStartedRef.current) {
        track("image_upload_abandoned", {
          step_number: 0,
          reason: "navigated_away",
        });
      }
    };
  }, []);

  // Advisory quality check. Any failure resolves to "usable" so a model or
  // network hiccup never blocks the user.
  const checkPhoto = useCallback(async (small: string) => {
    try {
      track("photo_check_started", { step_number: 0 });
      const { data, error } = await supabase.functions.invoke("check-room-photo", {
        body: { image: small },
      });
      if (error || !data) {
        return { usable: true, issues: [] as IssueCode[], raw: { error: String(error?.message ?? "no data") } };
      }
      const issues: IssueCode[] = Array.isArray(data.issues)
        ? data.issues.filter((i: string): i is IssueCode => i in ISSUE_COPY)
        : [];
      return { usable: data.usable !== false, issues, raw: data };
    } catch (e) {
      console.warn("Photo check failed; continuing:", e);
      return { usable: true, issues: [] as IssueCode[], raw: { error: String(e) } };
    }
  }, []);

  const accept = useCallback(
    (file: File, dataUrl: string) => {
      uploadStartedRef.current = false;
      track("image_upload_completed", {
        step_number: 0,
        file_size: file.size,
        file_type: file.type,
      });
      onUpload(file, dataUrl);
    },
    [onUpload],
  );

  const handleFile = useCallback(
    async (rawFile: File) => {
      const MAX_BYTES = 15 * 1024 * 1024; // 15 MB
      if (rawFile.size > MAX_BYTES) {
        toast({
          title: "Photo is too large",
          description: "Please upload an image under 15 MB.",
          variant: "destructive",
        });
        return;
      }
      const isHeic = await isHeicImage(rawFile);
      if (!rawFile.type.startsWith("image/") && !isHeic) return;
      uploadStartedRef.current = true;
      setPending(null);
      setCheckDebug(null);
      setHeldPhoto(null);
      track("image_upload_started", { step_number: 0 });
      setIsConverting(true);
      try {
        const file = await normalizeImageFile(rawFile);

        // Kick off the (cheap) pre-check on a small copy decoded straight from
        // the file, in parallel with the full-resolution data URL read.
        const readFull = new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target?.result as string);
          reader.onerror = () => reject(new Error("read_failed"));
          reader.readAsDataURL(file);
        }).then(async (full) => {
          // The generator only accepts JPEG/PNG/WebP up to 8 MB. Re-encode
          // anything larger or in another format (GIF, AVIF, TIFF…) to a
          // high-quality JPEG so the request isn't rejected.
          const okType = /^data:image\/(jpeg|png|webp);/i.test(full);
          const approxBytes = (full.length * 3) / 4;
          if (okType && approxBytes <= 6 * 1024 * 1024) return full;
          const reencoded =
            (await downscaleFile(file, 3072, 0.9)) ?? (await downscaleDataUrl(full, 3072, 0.9));
          return reencoded.startsWith("data:image/jpeg") ? reencoded : full;
        });

        const smallPromise = downscaleFile(file).then(
          async (small) => small ?? (await downscaleDataUrl(await readFull)),
        );

        setIsConverting(false);
        setIsChecking(true);
        const checkStartedAt = performance.now();
        const [checkResult, dataUrl] = await Promise.all([
          smallPromise.then(checkPhoto),
          readFull,
        ]);
        const { usable, issues } = checkResult;
        setIsChecking(false);
        const raw = (checkResult as any).raw ?? {};
        // Always record diagnostics so turning the toggle on later still shows
        // the last evaluation.
        setCheckDebug({
          usable,
          isRoom: raw?.isRoom,
          imageType: raw?.imageType ?? null,
          checked: raw?.checked,
          issues,
          debug: raw?.debug,
          client_ms: Math.round(performance.now() - checkStartedAt),
        });
        if (usable || issues.length === 0) {
          track("photo_check_passed", { step_number: 0 });
          if (debugMode) {
            // Hold the flow so the debug details stay on screen.
            setHeldPhoto({ file, dataUrl });
            return;
          }
          accept(file, dataUrl);
          return;
        }
        track("photo_check_failed", { step_number: 0, issues });
        setPending({ file, dataUrl, issues });
      } catch (error) {
        uploadStartedRef.current = false;
        setIsChecking(false);
        track("image_upload_abandoned", {
          step_number: 0,
          reason: "error",
        });
        if ((error as Error)?.message !== "read_failed") {
          toast({
            title: "Couldn't load HEIC image",
            description:
              error instanceof Error ? error.message : "Please upload the image as JPG or PNG.",
            variant: "destructive",
          });
        }
      } finally {
        setIsConverting(false);
      }
    },
    [accept, checkPhoto, debugMode],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files[0];
      if (file) handleFile(file);
    },
    [handleFile]
  );

  const busy = isConverting || isChecking;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="flex flex-col items-center gap-8 w-full max-w-lg mx-auto"
    >
      <div className="text-center space-y-3">
        <h1 className="text-2xl md:text-4xl font-display text-foreground" style={{ letterSpacing: "-0.96px" }}>
          Redesign Your Space
        </h1>
        <p className="text-muted-foreground text-lg">
          Upload a photo of your room and let AI transform it.
        </p>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*,.heic,.heif"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          if (file) handleFile(file);
        }}
      />

      {debugMode && (
        <div className="w-full border border-dashed border-foreground/30 bg-muted/30 p-4 space-y-3 text-left">
          <p className="flex items-center gap-2 text-xs font-medium text-foreground">
            <Bug className="w-3.5 h-3.5" />
            Debug — photo evaluation
          </p>
          {!checkDebug ? (
            <p className="text-[11px] text-muted-foreground">
              {busy
                ? "Evaluating the photo…"
                : "No photo evaluated yet — upload a photo to see the classifier output here."}
            </p>
          ) : (
          <>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
            <span>usable: <b className="text-foreground">{String(checkDebug.usable)}</b></span>
            <span>isRoom: <b className="text-foreground">{String(checkDebug.isRoom ?? "—")}</b></span>
            <span>imageType: <b className="text-foreground">{checkDebug.imageType ?? "—"}</b></span>
            <span>checked: <b className="text-foreground">{String(checkDebug.checked ?? "—")}</b></span>
            <span>issues: <b className="text-foreground">{checkDebug.issues.join(", ") || "none"}</b></span>
            <span>client: <b className="text-foreground">{checkDebug.client_ms}ms</b></span>
          </div>
          <pre className="whitespace-pre-wrap break-words font-mono text-[11px] text-foreground bg-muted/50 p-2 max-h-72 overflow-auto">
            {JSON.stringify(checkDebug.debug ?? {}, null, 2)}
          </pre>
          {heldPhoto && (
            <button
              type="button"
              onClick={() => {
                const photo = heldPhoto;
                setHeldPhoto(null);
                accept(photo.file, photo.dataUrl);
              }}
              className="rounded-full bg-gradient-cta text-primary-foreground px-5 py-2 text-sm font-semibold hover:opacity-95 transition-opacity"
            >
              Continue with this photo
            </button>
          )}
          </>
          )}
        </div>
      )}

      {pending ? (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full rounded-2xl border border-border bg-muted/40 p-5 space-y-4"
        >
          <div className="flex items-start gap-4">
            <img
              src={pending.dataUrl}
              alt="The room photo you uploaded"
              className="w-20 h-20 rounded-lg object-cover border border-border shrink-0"
            />
            <div className="min-w-0">
              <p className="flex items-center gap-2 font-display text-base font-medium text-foreground">
                <AlertTriangle className="w-4 h-4 shrink-0 text-destructive" />
                This photo may not redesign well
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                You can still use it — here's what we noticed.
              </p>
            </div>
          </div>

          <ul className="space-y-3">
            {pending.issues.map((issue) => (
              <li key={issue} className="text-sm">
                <p className="text-foreground font-medium">{ISSUE_COPY[issue].title}</p>
                <p className="text-muted-foreground mt-0.5">{ISSUE_COPY[issue].tip}</p>
              </li>
            ))}
          </ul>

          <div className="flex flex-col sm:flex-row gap-3 pt-1">
            <button
              type="button"
              onClick={() => {
                track("photo_check_replaced", { step_number: 0, issues: pending.issues });
                setPending(null);
                inputRef.current?.click();
              }}
              className="flex-1 rounded-full bg-gradient-cta text-primary-foreground px-5 py-2.5 text-sm font-semibold hover:opacity-95 transition-opacity"
            >
              Upload a different photo
            </button>
            <button
              type="button"
              onClick={() => {
                track("photo_check_overridden", { step_number: 0, issues: pending.issues });
                accept(pending.file, pending.dataUrl);
              }}
              className="flex-1 rounded-full border border-border text-foreground px-5 py-2.5 text-sm font-medium hover:bg-foreground/[0.04] transition-colors"
            >
              Use it anyway
            </button>
          </div>
        </motion.div>
      ) : (
        <label
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          className={`
            relative flex flex-col items-center justify-center w-full aspect-[4/3]
            border border-dashed transition-all duration-300
            ${busy ? "cursor-wait" : "cursor-pointer"}
            ${
              isDragging
                ? "border-foreground bg-foreground/5 scale-[1.02]"
                : "border-border hover:border-foreground/30 hover:bg-foreground/[0.03]"
            }
          `}
        >
          <input
            type="file"
            accept="image/*,.heic,.heif"
            disabled={busy}
            className="absolute inset-0 opacity-0 cursor-pointer disabled:cursor-wait"
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = "";
              if (file) handleFile(file);
            }}
          />
          <div className="flex flex-col items-center gap-4 pointer-events-none">
            <div className="w-16 h-16 flex items-center justify-center">
              {busy ? (
                <Loader2 className="w-8 h-8 text-foreground animate-spin" />
              ) : isDragging ? (
                <ImageIcon className="w-8 h-8 text-foreground" />
              ) : (
                <Upload className="w-8 h-8 text-muted-foreground" />
              )}
            </div>
            <div className="text-center">
              <p className="font-display text-lg font-medium text-foreground">
                {isConverting
                  ? "Converting photo…"
                  : isChecking
                  ? "Checking your photo…"
                  : isDragging
                  ? "Drop your photo here"
                  : "Drag & drop a room photo"}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                {isChecking
                  ? "Making sure we can see the whole room"
                  : isConverting
                  ? "This only takes a moment"
                  : "or click to browse"}
              </p>
            </div>
          </div>
        </label>
      )}

      <div className="w-full rounded-2xl border border-border bg-muted/40 p-5">
        <p className="font-display text-sm font-medium text-foreground mb-3">
          For best results
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
          <div>
            <p className="eyebrow mb-2">
              Do upload
            </p>
            <ul className="space-y-1.5">
              {[
                "Wide shot of the whole room",
                "Good, even lighting",
                "Walls, floor & a corner visible",
                "Tidy space, furniture clear",
              ].map((item) => (
                <li key={item} className="flex items-start gap-2 text-sm text-foreground">
                  <Check className="w-4 h-4 mt-0.5 shrink-0 text-foreground" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="eyebrow mb-2">
              Avoid
            </p>
            <ul className="space-y-1.5">
              {[
                "Floor plans or blueprints",
                "Close-ups of single furniture",
                "Cluttered or very dark rooms",
                "Filters, collages, screenshots",
              ].map((item) => (
                <li key={item} className="flex items-start gap-2 text-sm text-muted-foreground">
                  <X className="w-4 h-4 mt-0.5 shrink-0 text-destructive" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default PhotoUpload;
